import { type RefObject, useLayoutEffect, useRef } from "react";
import { z } from "zod";
import { sessionStore } from "../../../lib/store";

/**
 * Scroll positions keyed by URL pathname. Set when a user leaves a list by
 * clicking into an item, consumed (and cleared) when they return so that
 * forward navigation doesn't restore stale offsets.
 */
const scrollPositionsStore = sessionStore<Record<string, number>>(
  "scrollPositions",
  {},
  z.record(z.string(), z.number())
);

export function getScrollPosition(pathname: string): number {
  const stored = scrollPositionsStore.get()[pathname];
  return stored === undefined ? 0 : stored;
}

export function setScrollPosition(pathname: string, position: number): void {
  scrollPositionsStore.set((prev) => ({ ...prev, [pathname]: position }));
}

export function clearScrollPosition(pathname: string): void {
  scrollPositionsStore.set((prev) => {
    if (!Object.hasOwn(prev, pathname)) return prev;
    const { [pathname]: _, ...rest } = prev;
    return rest;
  });
}

/**
 * A feed that comes back where it was left: the feed unmounts when the reader
 * opens an article or asks JuisciGPT, and its scroller would otherwise come
 * back at the top. Owns the scroller's ref, restores the offset saved under
 * `key` once the feed has content to scroll through, then clears it.
 */
export function useFeedScrollMemory<Scroller extends HTMLElement>(
  key: string,
  hasContent: boolean
): {
  scrollRef: RefObject<Scroller>;
  /** The scroller's `onScroll`: records the offset under `key`. */
  rememberScroll: () => void;
  /** Switching to another feed (`nextKey`) always starts it at the top. */
  startAtTop: (nextKey: string) => void;
} {
  const scrollRef = useRef<Scroller>(null);
  useLayoutEffect(() => {
    if (!hasContent) return;
    const scroller = scrollRef.current;
    if (scroller === null)
      throw new Error("should not happen: the feed scroller is unmounted");
    const saved = getScrollPosition(key);
    if (saved === 0) return;
    scroller.scrollTop = saved;
    clearScrollPosition(key);
  }, [hasContent, key]);
  function currentScroller(): Scroller {
    const scroller = scrollRef.current;
    if (scroller === null)
      throw new Error("should not happen: the feed scroller is unmounted");
    return scroller;
  }
  return {
    scrollRef,
    rememberScroll: () => {
      const y = currentScroller().scrollTop;
      // Back at the top is a place too: an older offset must not outlive it.
      if (y > 0) setScrollPosition(key, y);
      else clearScrollPosition(key);
    },
    startAtTop: (nextKey) => {
      // Otherwise the restore above would jump the destination back down.
      clearScrollPosition(nextKey);
      currentScroller().scrollTop = 0;
    },
  };
}
